/** Eight certificate-depth examples per part for Hypothesis Testing. */

export const PS_HYP_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "State the hypotheses correctly",
    setup: "A factory claims its bags weigh $50$ kg. Inspectors suspect underfilling. Write $H_0$, $H_1$, and describe the rejection region at $\\alpha=0.05$.",
    steps: [
      { text: "$H_0$ always states the status quo with equality: $H_0:\\mu=50$.", why: "Tests are built assuming $H_0$ is true." },
      { text: "The suspicion is underfilling, so $H_1:\\mu<50$.", why: "The alternative encodes the concern." },
      { text: "This is a one-tailed (left-tailed) test.", why: "The inequality points left." },
      { text: "At $\\alpha=0.05$ with known $\\sigma$, the critical value is $z_{0.05}=-1.645$.", why: "All $5\\%$ sits in the left tail." },
      { text: "Rejection region: $z<-1.645$.", why: "Decision rule fixed before seeing data." },
      { text: "A two-tailed test would instead use $\\pm1.96$.", why: "Comparison for context." },
      { text: "$\\alpha=0.05$ means a $5\\%$ chance of rejecting a truthful factory.", why: "Type I error interpretation." },
      { text: "Sample evidence above $50$ can never support $H_1$, no matter how large.", why: "One-tailed logic." },
    ],
    result: "$H_0:\\mu=50$ versus $H_1:\\mu<50$; reject when $z<-1.645$",
    check: "The inequality in $H_1$ matches the direction of the inspectors' concern.",
    mistake: "Putting the claim to be proved in $H_0$; the burden of proof sits with $H_1$.",
    life: "Regulators write hypotheses this way so that a manufacturer is presumed compliant until the data are strong enough to say otherwise.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "One-sample z-test with known sigma",
    setup: "$n=36$, $\\bar{x}=48.5$, $\\sigma=3$ known. Test $H_0:\\mu=50$ against $H_1:\\mu<50$ at $\\alpha=0.05$.",
    steps: [
      { text: "Standard error $=\\sigma/\\sqrt{n}=3/6=0.5$.", why: "Spread of the sample mean." },
      { text: "Test statistic $z=\\dfrac{\\bar{x}-\\mu_0}{\\mathrm{SE}}$.", why: "Standardize the observed mean." },
      { text: "$z=\\dfrac{48.5-50}{0.5}=-3.0$.", why: "Substitute." },
      { text: "Critical value for a left tail at $5\\%$ is $-1.645$.", why: "Decision boundary." },
      { text: "$-3.0<-1.645$, so reject $H_0$.", why: "The statistic lies in the rejection region." },
      { text: "p-value $=P(Z<-3)\\approx0.00135$.", why: "Strength of evidence." },
      { text: "Since $0.00135<0.05$, the same conclusion follows.", why: "Critical value and p-value always agree." },
      { text: "Conclusion: strong evidence the bags are underfilled.", why: "State the result in context." },
    ],
    result: "$z=-3.0$, p $\\approx0.0014$ — reject $H_0$; the mean is below $50$ kg",
    check: "The observed mean is three standard errors below the claim — far outside sampling noise.",
    mistake: "Dividing by $\\sigma$ instead of $\\sigma/\\sqrt{n}$, which would give $z=-0.5$ and the opposite conclusion.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "One-sample t-test with unknown sigma",
    setup: "$n=16$, $\\bar{x}=52$, $s=4$. Test $H_0:\\mu=50$ against $H_1:\\mu\\ne50$ at $\\alpha=0.05$.",
    steps: [
      { text: "$\\sigma$ is unknown and estimated by $s$, so use the $t$ distribution.", why: "Extra uncertainty widens the tails." },
      { text: "Degrees of freedom $=n-1=15$.", why: "One parameter estimated." },
      { text: "Standard error $=s/\\sqrt{n}=4/4=1$.", why: "Compute the denominator." },
      { text: "$t=\\dfrac{52-50}{1}=2.0$.", why: "Test statistic." },
      { text: "Two-tailed critical value: $t_{0.025,15}=2.131$.", why: "Split $\\alpha$ across both tails." },
      { text: "$|2.0|<2.131$, so do not reject $H_0$.", why: "Statistic falls inside the acceptance region." },
      { text: "p-value $\\approx2(0.032)=0.064>0.05$.", why: "Consistent conclusion." },
      { text: "Note the same $t=2.0$ would be significant with $z$ ($1.96$) — small samples demand more evidence.", why: "Why $t$ matters." },
    ],
    result: "$t=2.0$ with $15$ df, p $\\approx0.064$ — fail to reject $H_0$",
    check: "The $95\\%$ confidence interval $52\\pm2.131(1)=[49.87,54.13]$ contains $50$, matching the decision.",
    mistake: "Using the $z$ critical value $1.96$ for a small sample with estimated $\\sigma$.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Test for a population proportion",
    setup: "A candidate claims $60\\%$ support. In a sample of $400$, $220$ support her. Test $H_0:p=0.6$ vs $H_1:p<0.6$ at $\\alpha=0.05$.",
    steps: [
      { text: "Sample proportion $\\hat{p}=220/400=0.55$.", why: "Point estimate." },
      { text: "Under $H_0$, the standard error uses $p_0$: $\\sqrt{\\dfrac{0.6(0.4)}{400}}$.", why: "Null value, not $\\hat{p}$, for the test." },
      { text: "$=\\sqrt{0.0006}=0.02449$.", why: "Compute." },
      { text: "$z=\\dfrac{0.55-0.60}{0.02449}\\approx-2.04$.", why: "Standardize." },
      { text: "Validity: $np_0=240$ and $n(1-p_0)=160$, both well above $5$.", why: "Normal approximation is safe." },
      { text: "Left-tail critical value $-1.645$; since $-2.04<-1.645$, reject $H_0$.", why: "Decision." },
      { text: "p-value $=P(Z<-2.04)\\approx0.0207$.", why: "Evidence strength." },
      { text: "Conclusion: evidence that true support is below $60\\%$.", why: "Contextual statement." },
    ],
    result: "$z\\approx-2.04$, p $\\approx0.021$ — reject $H_0$",
    check: "A $95\\%$ CI using $\\hat{p}$ is $0.55\\pm1.96(0.0249)=[0.501,0.599]$, excluding $0.6$.",
    mistake: "Using $\\hat{p}(1-\\hat{p})$ in the standard error of the test statistic instead of $p_0(1-p_0)$.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Paired-sample t-test",
    setup: "Five students score before and after a workshop: differences (after $-$ before) are $4,2,5,1,3$. Test for improvement at $\\alpha=0.05$.",
    steps: [
      { text: "Pairing removes between-student variability, so analyze the differences directly.", why: "Correct design for repeated measures." },
      { text: "$\\bar{d}=\\dfrac{4+2+5+1+3}{5}=3$.", why: "Mean difference." },
      { text: "Deviations: $1,-1,2,-2,0$; squares $1,1,4,4,0$ summing to $10$.", why: "Toward the variance." },
      { text: "$s_d^{2}=10/4=2.5$, so $s_d\\approx1.581$.", why: "Divide by $n-1$." },
      { text: "Standard error $=1.581/\\sqrt5\\approx0.707$.", why: "SE of the mean difference." },
      { text: "$t=\\dfrac{3-0}{0.707}\\approx4.24$ with $4$ df.", why: "$H_0:\\mu_d=0$." },
      { text: "One-tailed critical value $t_{0.05,4}=2.132$.", why: "Improvement is directional." },
      { text: "$4.24>2.132$, so reject $H_0$: the workshop improved scores.", why: "Decision and conclusion." },
    ],
    result: "$t\\approx4.24$ with $4$ df — significant improvement",
    check: "Every difference is positive, so a significant result is unsurprising.",
    mistake: "Running a two-independent-sample test and throwing away the pairing, which inflates the standard error.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Two independent sample means",
    setup: "Group A: $n_1=40$, $\\bar{x}_1=75$, $s_1=8$. Group B: $n_2=50$, $\\bar{x}_2=71$, $s_2=10$. Test $\\mu_1=\\mu_2$ at $\\alpha=0.05$.",
    steps: [
      { text: "$H_0:\\mu_1-\\mu_2=0$, $H_1:\\mu_1-\\mu_2\\ne0$.", why: "Two-tailed comparison." },
      { text: "Variances of the two means: $\\dfrac{64}{40}=1.6$ and $\\dfrac{100}{50}=2.0$.", why: "Independent samples add variances." },
      { text: "Standard error $=\\sqrt{1.6+2.0}=\\sqrt{3.6}\\approx1.897$.", why: "Root of the sum." },
      { text: "Observed difference $=75-71=4$.", why: "Numerator." },
      { text: "$z\\approx\\dfrac{4}{1.897}\\approx2.11$.", why: "Both samples are large, so $z$ is adequate." },
      { text: "Critical values $\\pm1.96$; since $2.11>1.96$, reject $H_0$.", why: "Decision." },
      { text: "p-value $=2P(Z>2.11)\\approx0.035$.", why: "Two-tailed." },
      { text: "A $95\\%$ CI for the difference: $4\\pm1.96(1.897)=[0.28,7.72]$, which excludes $0$.", why: "Effect-size view of the same result." },
    ],
    result: "$z\\approx2.11$, p $\\approx0.035$ — the means differ significantly",
    check: "The interval barely excludes $0$, matching a p-value just under $0.05$.",
    mistake: "Adding standard deviations instead of variances when combining two samples.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Chi-square goodness of fit",
    setup: "A die is rolled $120$ times giving counts $15,23,18,22,17,25$. Test fairness at $\\alpha=0.05$.",
    steps: [
      { text: "$H_0$: the die is fair, so each face has probability $\\tfrac16$.", why: "Null model." },
      { text: "Expected count per face $=120/6=20$.", why: "$np$ under $H_0$." },
      { text: "Deviations $O-E$: $-5,3,-2,2,-3,5$.", why: "They must sum to zero." },
      { text: "Squared deviations: $25,9,4,4,9,25$.", why: "Prepare the statistic." },
      { text: "$\\chi^{2}=\\sum\\dfrac{(O-E)^{2}}{E}=\\dfrac{25+9+4+4+9+25}{20}$.", why: "Common denominator since all $E$ are equal." },
      { text: "$=\\dfrac{76}{20}=3.8$.", why: "Test statistic." },
      { text: "Degrees of freedom $=6-1=5$; critical value $\\chi^{2}_{0.05,5}=11.07$.", why: "One constraint (total count)." },
      { text: "$3.8<11.07$, so do not reject $H_0$: no evidence of bias.", why: "Decision." },
    ],
    result: "$\\chi^{2}=3.8$ with $5$ df — the die appears fair",
    check: "All expected counts are $20\\ge5$, so the chi-square approximation is valid.",
    mistake: "Dividing by $O$ instead of $E$ in the statistic.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Sample size for a desired margin of error",
    setup: "How many observations are needed to estimate $\\mu$ within $\\pm2$ units at $95\\%$ confidence when $\\sigma=10$? And for $\\pm1$?",
    steps: [
      { text: "Margin of error: $E=z_{\\alpha/2}\\dfrac{\\sigma}{\\sqrt{n}}$.", why: "Half-width of the confidence interval." },
      { text: "Solve for $n$: $n=\\left(\\dfrac{z\\sigma}{E}\\right)^{2}$.", why: "Rearrange." },
      { text: "With $z=1.96$, $\\sigma=10$, $E=2$: $n=\\left(\\dfrac{19.6}{2}\\right)^{2}$.", why: "Substitute." },
      { text: "$=9.8^{2}=96.04$.", why: "Compute." },
      { text: "Always round up: $n=97$.", why: "Rounding down would exceed the allowed margin." },
      { text: "For $E=1$: $n=(19.6)^{2}=384.16$, so $n=385$.", why: "Halving the margin." },
      { text: "Precision costs quadratically: halving $E$ multiplies $n$ by four.", why: "Key planning insight." },
      { text: "At $99\\%$ confidence ($z=2.576$) with $E=2$: $n=(12.88)^{2}=165.9\\to166$.", why: "Higher confidence also costs sample size." },
    ],
    result: "$n=97$ for $\\pm2$; $n=385$ for $\\pm1$; $n=166$ for $\\pm2$ at $99\\%$",
    check: "With $n=97$: $1.96(10)/\\sqrt{97}\\approx1.99\\le2$. Requirement met.",
    mistake: "Rounding $96.04$ down to $96$, which leaves the margin slightly above $2$.",
  },
];

export const PS_HYP_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Compute and interpret a p-value",
    setup: "A two-tailed z-test gives $z=1.78$. Find the p-value and decide at $\\alpha=0.05$ and $\\alpha=0.10$.",
    steps: [
      { text: "For a two-tailed test, p $=2P(Z>|z|)$.", why: "Both tails count as extreme." },
      { text: "$\\Phi(1.78)\\approx0.9625$.", why: "Table lookup." },
      { text: "Upper tail $=1-0.9625=0.0375$.", why: "One side." },
      { text: "p $=2(0.0375)=0.075$.", why: "Double it." },
      { text: "At $\\alpha=0.05$: $0.075>0.05$, so do not reject $H_0$.", why: "Decision rule." },
      { text: "At $\\alpha=0.10$: $0.075<0.10$, so reject $H_0$.", why: "The same data, a different threshold." },
      { text: "The p-value is $P(\\text{data this extreme}\\mid H_0)$, not $P(H_0)$.", why: "Standard misinterpretation to avoid." },
      { text: "Reporting p $=0.075$ is more informative than \"not significant\".", why: "Best practice." },
    ],
    result: "p $=0.075$: retain $H_0$ at $5\\%$ but reject at $10\\%$",
    check: "A one-tailed test on the same data would give p $=0.0375$, significant at $5\\%$.",
    mistake: "Reading the p-value as the probability that the null hypothesis is true.",
    life: "Drug trials pre-register $\\alpha$ precisely so that a borderline p-value like this cannot be reinterpreted after the fact.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "One-tailed versus two-tailed p-values",
    setup: "A t-test with $20$ df gives $t=1.85$. Compare the one- and two-tailed conclusions at $\\alpha=0.05$.",
    steps: [
      { text: "Critical value for a one-tailed test: $t_{0.05,20}=1.725$.", why: "All $5\\%$ in one tail." },
      { text: "$1.85>1.725$, so a one-tailed test rejects $H_0$.", why: "Decision." },
      { text: "One-tailed p-value $\\approx0.0396$.", why: "Just under $0.05$." },
      { text: "Two-tailed critical value: $t_{0.025,20}=2.086$.", why: "$\\alpha$ split in half." },
      { text: "$1.85<2.086$, so a two-tailed test does not reject.", why: "Opposite decision." },
      { text: "Two-tailed p $=2(0.0396)=0.0792$.", why: "Double the tail area." },
      { text: "The direction must be chosen before collecting data.", why: "Otherwise the test is invalid." },
      { text: "Switching to one-tailed after seeing the sign inflates the true Type I error to roughly $10\\%$.", why: "Why pre-registration exists." },
    ],
    result: "One-tailed: reject (p $\\approx0.040$); two-tailed: retain (p $\\approx0.079$)",
    check: "The two-tailed p-value is exactly twice the one-tailed value here, as expected for a symmetric distribution.",
    mistake: "Choosing the tail direction after inspecting the data.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Type I and Type II errors in context",
    setup: "A smoke alarm test has $H_0$: no fire. Classify the two error types and their costs, and relate them to $\\alpha$ and $\\beta$.",
    steps: [
      { text: "Type I error: rejecting $H_0$ when it is true — a false alarm with no fire.", why: "Definition." },
      { text: "Its probability is $\\alpha$, chosen by the designer.", why: "Significance level." },
      { text: "Type II error: failing to reject $H_0$ when it is false — a real fire missed.", why: "Definition." },
      { text: "Its probability is $\\beta$, and power $=1-\\beta$.", why: "Detection ability." },
      { text: "Lowering $\\alpha$ (a less trigger-happy alarm) raises $\\beta$ for a fixed sensor.", why: "The fundamental trade-off." },
      { text: "Here a missed fire is far costlier, so designers accept a high $\\alpha$ to push $\\beta$ near zero.", why: "Cost-weighted choice." },
      { text: "The only way to reduce both errors at once is a better sensor or more data.", why: "Increasing $n$ shifts the whole trade-off curve." },
      { text: "In a courtroom the priority flips: $H_0$ is innocence, and $\\alpha$ is kept tiny.", why: "Shows the choice is contextual, not mathematical." },
    ],
    result: "Type I $=$ false alarm (probability $\\alpha$); Type II $=$ missed fire (probability $\\beta$, power $1-\\beta$)",
    check: "With $\\alpha=0.05$ and power $0.80$, the design accepts a $20\\%$ miss rate at the target effect size.",
    mistake: "Assuming $\\alpha+\\beta=1$; the two refer to different states of the world.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Compute power for a specific alternative",
    setup: "Test $H_0:\\mu=100$ vs $H_1:\\mu>100$ with $\\sigma=15$, $n=25$, $\\alpha=0.05$. Find the power when $\\mu=105$.",
    steps: [
      { text: "Standard error $=15/\\sqrt{25}=3$.", why: "Sampling distribution of $\\bar{X}$." },
      { text: "Rejection rule: $z>1.645$, i.e. $\\bar{x}>100+1.645(3)$.", why: "Convert to the data scale." },
      { text: "Critical mean $=104.935$.", why: "Threshold." },
      { text: "Under $\\mu=105$, $\\bar{X}\\sim N(105,3^{2})$.", why: "Shift the sampling distribution." },
      { text: "Standardize the threshold: $z=\\dfrac{104.935-105}{3}\\approx-0.0217$.", why: "Relative to the true mean." },
      { text: "Power $=P(Z>-0.0217)\\approx0.509$.", why: "Area to the right." },
      { text: "So there is only about a $51\\%$ chance of detecting a $5$-unit shift.", why: "Interpretation." },
      { text: "For $n=100$: SE $=1.5$, threshold $102.47$, $z=-1.69$, power $\\approx0.954$.", why: "Quadrupling $n$ nearly doubles detection ability." },
    ],
    result: "Power $\\approx0.51$ at $\\mu=105$ (rising to $\\approx0.95$ with $n=100$)",
    check: "Power must exceed $\\alpha=0.05$ for any true alternative, and it does.",
    mistake: "Computing power under $H_0$ rather than under the specific alternative.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Confidence interval and test agree",
    setup: "$n=25$, $\\bar{x}=104$, $s=10$. Build a $95\\%$ CI for $\\mu$ and test $H_0:\\mu=100$ at $\\alpha=0.05$.",
    steps: [
      { text: "$\\sigma$ unknown, so use $t$ with $24$ df: $t_{0.025,24}=2.064$.", why: "Small-sample interval." },
      { text: "Standard error $=10/5=2$.", why: "Compute." },
      { text: "Margin of error $=2.064(2)=4.128$.", why: "Half-width." },
      { text: "CI $=104\\pm4.128=[99.87,\\,108.13]$.", why: "Interval estimate." },
      { text: "The interval contains $100$, so $H_0$ is not rejected at $5\\%$.", why: "Duality of intervals and two-tailed tests." },
      { text: "Direct test: $t=\\dfrac{104-100}{2}=2.0$.", why: "Test statistic." },
      { text: "$|2.0|<2.064$, so retain $H_0$ — the same answer.", why: "Consistency confirmed." },
      { text: "p-value $\\approx0.057$, just above $0.05$, matching the interval's near-miss.", why: "Three views of one result." },
    ],
    result: "CI $[99.87,108.13]$ and $t=2.0$ both retain $H_0$ (p $\\approx0.057$)",
    check: "A $90\\%$ CI would be $104\\pm1.711(2)=[100.58,107.42]$, excluding $100$ — matching rejection at $\\alpha=0.10$.",
    mistake: "Reporting a significant result because the interval nearly misses the null value.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Multiple testing inflates false positives",
    setup: "Twenty independent tests are run at $\\alpha=0.05$ with all nulls true. Find the chance of at least one false positive and apply a Bonferroni correction.",
    steps: [
      { text: "Each test has a $0.05$ chance of a false positive.", why: "Definition of $\\alpha$." },
      { text: "$P(\\text{no false positive on one test})=0.95$.", why: "Complement." },
      { text: "Independence: $P(\\text{none in }20)=0.95^{20}$.", why: "Multiply." },
      { text: "$0.95^{20}\\approx0.3585$.", why: "Compute the power." },
      { text: "$P(\\text{at least one})\\approx1-0.3585=0.6415$.", why: "Complement rule." },
      { text: "So a $64\\%$ chance of a spurious \"discovery\" — the multiplicity problem.", why: "Interpretation." },
      { text: "Bonferroni: use $\\alpha^{*}=0.05/20=0.0025$ per test.", why: "Control the family-wise error rate." },
      { text: "Then $1-0.9975^{20}\\approx0.0489\\le0.05$.", why: "Family-wise rate restored." },
    ],
    result: "$64\\%$ chance of at least one false positive; Bonferroni's $\\alpha^{*}=0.0025$ restores $5\\%$ overall",
    check: "The Bonferroni bound $20(0.0025)=0.05$ brackets the exact $0.0489$.",
    mistake: "Reporting the single most significant of many tests as if only one test had been run.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Statistical significance versus practical importance",
    setup: "A drug lowers blood pressure by $0.4$ mmHg on average; $n=40000$, $s=10$. Test significance and judge importance.",
    steps: [
      { text: "Standard error $=10/\\sqrt{40000}=10/200=0.05$.", why: "Huge sample, tiny SE." },
      { text: "$z=\\dfrac{0.4}{0.05}=8.0$.", why: "Test statistic." },
      { text: "p-value $<10^{-14}$, overwhelmingly significant.", why: "Far in the tail." },
      { text: "$95\\%$ CI: $0.4\\pm1.96(0.05)=[0.302,0.498]$ mmHg.", why: "Effect size with uncertainty." },
      { text: "Effect size $d=\\dfrac{0.4}{10}=0.04$ standard deviations.", why: "Cohen's $d$ is scale-free." },
      { text: "By convention $d=0.04$ is negligible ($0.2$ counts as small).", why: "Benchmark." },
      { text: "Clinically, a $0.4$ mmHg drop does not change treatment decisions.", why: "Practical judgement." },
      { text: "Large $n$ makes trivial effects detectable; always report the interval alongside the p-value.", why: "The lesson." },
    ],
    result: "Highly significant ($z=8$, p $<10^{-14}$) but practically negligible ($d=0.04$)",
    check: "With $n=100$ the same effect gives $z=0.4$ and p $\\approx0.69$ — significance is sample-size driven here.",
    mistake: "Equating a tiny p-value with a large or important effect.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Balancing errors by choosing the threshold",
    setup: "A quality test rejects a batch when $\\bar{x}>c$, with $\\sigma=2$, $n=16$. Good batches have $\\mu=10$, bad ones $\\mu=11$. Find $c$ so that $\\alpha=\\beta$.",
    steps: [
      { text: "Standard error $=2/\\sqrt{16}=0.5$.", why: "Both distributions share it." },
      { text: "Under good batches, $\\bar{X}\\sim N(10,0.5^{2})$; under bad ones, $N(11,0.5^{2})$.", why: "Two competing models." },
      { text: "$\\alpha=P(\\bar{X}>c\\mid\\mu=10)$ and $\\beta=P(\\bar{X}\\le c\\mid\\mu=11)$.", why: "Definitions." },
      { text: "Equal errors require equal standardized distances: $\\dfrac{c-10}{0.5}=\\dfrac{11-c}{0.5}$.", why: "Symmetry of the normal." },
      { text: "So $c-10=11-c$, giving $2c=21$.", why: "Solve." },
      { text: "$c=10.5$ — the midpoint, as symmetry suggests.", why: "Threshold found." },
      { text: "Then $z=\\dfrac{10.5-10}{0.5}=1$, so $\\alpha=\\beta=P(Z>1)\\approx0.1587$.", why: "Common error rate." },
      { text: "To cut both to $5\\%$, raise $n$: $n=\\left(\\dfrac{2(1.645)(2)}{1}\\right)^{2}\\approx43.3\\to44$.", why: "Only more data improves both at once." },
    ],
    result: "$c=10.5$ gives $\\alpha=\\beta\\approx0.159$; reaching $5\\%$ each needs $n\\approx44$",
    check: "At $c=10.5$ both distributions are exactly one standard error away, so the errors must match.",
    mistake: "Assuming a lower threshold reduces both error types; it merely trades $\\beta$ for $\\alpha$.",
  },
];
